import { useSignal } from "@preact/signals";
import { IcoGripVertical } from "../../../../components/icons";
import type { Question } from "./types";

type ReorderListProps = {
  questions: Question[];
  showArchived: boolean;
  onReorder: (reorderedQuestions: Question[]) => void;
};

export function ReorderList({
  questions,
  showArchived,
  onReorder,
}: ReorderListProps) {
  const dragIndex = useSignal<number | null>(null);
  const dropIndex = useSignal<number | null>(null);

  const visibleQuestions = questions
    .filter((q) => showArchived || !q.archived)
    .sort((a, b) => a.seq - b.seq);

  function handleDragStart(e: DragEvent, index: number) {
    dragIndex.value = index;
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", String(index));
    }
  }

  function handleDragOver(e: DragEvent, index: number) {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = "move";
    }
    dropIndex.value = index;
  }

  function handleDragEnd() {
    if (dragIndex.value !== null && dropIndex.value !== null) {
      const newQuestions = [...questions];
      const visibleIds = visibleQuestions.map((q) => q.id);

      // Get the dragged and target question IDs
      const draggedId = visibleIds[dragIndex.value];
      const targetId = visibleIds[dropIndex.value];

      if (draggedId && targetId && draggedId !== targetId) {
        // Find positions in full array
        const fromIndex = newQuestions.findIndex((q) => q.id === draggedId);
        const toIndex = newQuestions.findIndex((q) => q.id === targetId);

        if (fromIndex !== -1 && toIndex !== -1) {
          // Remove from old position and insert at new position
          const [moved] = newQuestions.splice(fromIndex, 1);
          if (moved) {
            newQuestions.splice(toIndex, 0, moved);
          }

          // Update sequence numbers
          const reordered = newQuestions.map((q, i) => ({ ...q, seq: i }));
          onReorder(reordered);
        }
      }
    }

    dragIndex.value = null;
    dropIndex.value = null;
  }

  function handleDragLeave() {
    dropIndex.value = null;
  }

  return (
    <div class="flex flex-col gap-1">
      {visibleQuestions.map((question, index) => (
        <div
          key={question.id}
          draggable={!question.archived}
          onDragStart={(e) => handleDragStart(e, index)}
          onDragOver={(e) => handleDragOver(e, index)}
          onDragEnd={handleDragEnd}
          onDragLeave={handleDragLeave}
          class={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
            dragIndex.value === index
              ? "opacity-50 border-border"
              : dropIndex.value === index
                ? "border-text-subtle bg-surface-hover"
                : "border-border bg-surface"
          } ${question.archived ? "opacity-50" : "cursor-move"}`}
        >
          {!question.archived && (
            <IcoGripVertical class="size-5 text-text-subtle flex-shrink-0" />
          )}
          <span class="text-sm font-medium text-text-muted">{index + 1}.</span>
          {question.title ? (
            <span
              class={`flex-1 font-medium truncate [&>*]:inline ${question.archived ? "text-text-muted line-through" : "text-text"}`}
              dangerouslySetInnerHTML={{ __html: question.title }}
            />
          ) : (
            <span class="flex-1 font-medium truncate text-text-muted">
              Untitled question
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
